/**
 * millage — rate-display unit conversion (Government data-model §2.1).
 *
 * A jurisdiction's stored `tax_rate` is expressed in the unit its
 * `is_millage_rate` flag declares: a millage rate is per $1,000, otherwise the
 * rate is per $100. The calc engine keeps that meaning (it picks the ÷1000 vs
 * ÷100 divisor from the same flag) — so the STORED number and its semantics are
 * never touched here.
 *
 * The Annual-Information panel offers a DISPLAY-ONLY toggle that lets an operator
 * VIEW the same rate in either unit. Switching units is a pure decimal shift: the
 * same economic rate reads as `2.5` per $100 or `25` mills per $1,000. Nothing is
 * persisted by toggling — this module only reshapes the number for display.
 */

export type RateUnit = 'per100' | 'per1000';

/** The unit the stored `tax_rate` is expressed in, per the millage flag. */
export function storedRateUnit(isMillage: boolean | number | null | undefined): RateUnit {
  return isMillage === true || isMillage === 1 ? 'per1000' : 'per100';
}

/**
 * The stored rate re-expressed in `displayUnit`, without mutating the stored
 * value. per100 → per1000 shifts the decimal one place right (× 10); the reverse
 * shifts it left (÷ 10). Returns null when there is no rate to show.
 */
export function displayRate(
  storedRate: number | null | undefined,
  isMillage: boolean | number | null | undefined,
  displayUnit: RateUnit,
): number | null {
  if (storedRate === null || storedRate === undefined || Number.isNaN(Number(storedRate))) {
    return null;
  }
  const rate = Number(storedRate);
  const from = storedRateUnit(isMillage);
  if (from === displayUnit) return rate;
  // Round to 6 dp so a ×10 / ÷10 shift doesn't surface binary-float noise.
  const shifted = displayUnit === 'per1000' ? rate * 10 : rate / 10;
  return Math.round(shifted * 1e6) / 1e6;
}

/** Human label for a rate unit — used beside the displayed number. */
export function rateUnitLabel(unit: RateUnit): string {
  return unit === 'per1000' ? 'per $1,000 (mills)' : 'per $100';
}
